'use client';

import clsx from 'clsx';
import { makeSlotId } from 'tempest-common';
import type { Post } from '@/types/entities';
import { AdSlot } from '@/components/AdSlot';
import { usePathname } from 'next/navigation';
import { useMemo, useState, useEffect } from 'react';
import { useSiteContext } from '@/context/SiteContext';
import { Newsletter } from '@/components/raven/Newsletter';
import { useBreakpoint } from '@/utils/hooks/use-breakpoint';
import { AddPlacement } from '@/context/GoogleAnalyticsContext';
import { getReservedHeightForVariant } from '@/utils/smart-slots';
import { SmartSlots, typeGuards } from '@themaven-net/smart-slots';
import type {
	CheerioElement,
	SmartSlotsDeps,
	SmartSlotConfig,
} from '@themaven-net/smart-slots';

import styles from './styles.module.css';

export { SmartSlotWidget } from './Widget';

type StandaloneSmartSlotProps = {
	item: Partial<Post>;
	variant: string;
};

// Helper function to validate smart slot configuration
const isValidSmartSlotConfig = (
	smartSlotConfig: SmartSlotConfig | undefined,
	variantWidget: { widgetType?: string } | undefined,
	slotType?: string,
) => {
	return (
		smartSlotConfig?.widgets &&
		variantWidget &&
		variantWidget.widgetType &&
		(slotType === undefined || slotType === 'standalone')
	);
};

// 1. This can happen if the variant is in markup but was removed from the config

export const SmartSlot = ({ item, variant }: StandaloneSmartSlotProps) => {
	const { config } = useSiteContext();
	const pathname = usePathname();
	const breakpoint = useBreakpoint();
	const [ready, setReady] = useState(false);
	const { smartSlotConfig } = config;

	const variantWidget = smartSlotConfig?.widgets?.[variant];
	const { slotType } = variantWidget ?? {};

	const defaultRuleSets = useMemo(() => {
		// Early return if configuration is invalid
		// 1.
		if (!isValidSmartSlotConfig(smartSlotConfig, variantWidget)) {
			return [];
		}

		// Safely access ruleSets with fallback
		const defaultRules = smartSlotConfig.ruleSets?.default ?? [];

		const filteredRules = defaultRules.filter(
			(rule) => rule.variant === variant,
		);

		if (filteredRules.length) {
			// If there are rules for the variant, we return them
			return filteredRules;
		}

		// Create fallback rule since we already validated the widget
		return [
			{
				variant,
				where: [{ and: [] }],
				widgetType: variantWidget.widgetType,
			},
		];
	}, [smartSlotConfig, variant, variantWidget]);

	const smartSlots = useMemo(() => {
		// Early return if configuration is invalid or not standalone
		// 1.
		if (!isValidSmartSlotConfig(smartSlotConfig, variantWidget, slotType)) {
			return null;
		}

		const variantOnlySmartSlotsConfig: SmartSlotConfig = {
			...smartSlotConfig,
			ruleSets: {
				default: defaultRuleSets,
			},
			widgets: {
				[variant]: variantWidget,
			},
		};

		const smartSlotsInstance = new SmartSlots(
			{
				contentItem: item as unknown as SmartSlotsDeps['contentItem'],
				contentSizes: {
					A: 'short',
					B: 'short',
					C: 'short',
					D: 'short',
				},
				enabled: true,
				injectionType: 'standalone',
				markup: '',
				path: pathname,
			},
			variantOnlySmartSlotsConfig,
		);

		return smartSlotsInstance;
	}, [
		item,
		pathname,
		defaultRuleSets,
		slotType,
		smartSlotConfig,
		variant,
		variantWidget,
	]);

	const slot = useMemo(() => {
		if (breakpoint && smartSlots) {
			const fakeInjectionPoint = '<div></div>' as unknown as CheerioElement;
			const result = smartSlots.addSmartSlot(
				breakpoint,
				fakeInjectionPoint,
				null,
				{ variant }, // Pass the variant directly to bypass rule evaluation
			);

			return result.widget;
		}
		return null;
	}, [breakpoint, smartSlots, variant]);

	useEffect(() => {
		setReady(true);
	}, []);

	// Early return if configuration is invalid
	// Some of this logic is duplicated in the useMemo hooks above
	// because conditional code is not supported before hooks
	// 1.
	if (!isValidSmartSlotConfig(smartSlotConfig, variantWidget)) {
		return null;
	}

	let component = null;

	// 1. We will render the configured fallback widget
	//    instead in the future.
	//    WOR-2554
	switch (slot?.widgetType) {
		case 'Newsletters':
			component = typeGuards.isNewslettersWidget(slot) && (
				<div className={clsx(styles.newsletters)}>
					<Newsletter
						newsletterIds={slot.config.newsletterIds}
						placement="slot"
					/>
				</div>
			);
			break;
		case 'Ad': {
			const zone = slot.config.zone || 'in_content';
			component = typeGuards.isAdWidget(slot) && (
				<AdSlot
					id={makeSlotId(zone)}
					refresh={slot.config.refreshRate}
					reserveHeight
					zone={zone}
				/>
			);
			break;
		}
		default:
			component = null;
	}

	const reservedHeight = getReservedHeightForVariant(
		breakpoint || 'D',
		smartSlotConfig,
		variant,
	);

	return (
		<div
			style={{
				...(reservedHeight && { minHeight: `${reservedHeight}px` }),
			}}
		>
			{ready && <AddPlacement placement="slot">{component}</AddPlacement>}
		</div>
	);
};
